/**
 * Live launch-pricing source of truth.
 * Endpoint: GET https://jobhacki.com/api/price
 *   → { price: "$7", count, cap, remaining, one_time, checkout }
 *
 * Cached at module level (sticky for the page lifetime, refreshed on next
 * full-page load). Never hardcode prices — always render via <PriceTag />
 * or usePrice() so the launch climb from $7 → $197 reflects in real time.
 */
import { useEffect, useState } from "react";

const DEFAULT = {
  price: "$7",
  count: 0,
  cap: 2500,
  remaining: 2500,
  one_time: true,
  checkout: "https://whop.com/checkout/plan_hfsvR7lsOjQds",
};

let cache = null;
let inflight = null;
const subs = new Set();

function notify() {
  for (const fn of subs) fn(cache);
}

export function getPrice() {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = fetch("https://jobhacki.com/api/price", { credentials: "omit" })
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      cache = d && d.price ? { ...DEFAULT, ...d } : DEFAULT;
      inflight = null;
      notify();
      return cache;
    })
    .catch(() => {
      cache = DEFAULT;
      inflight = null;
      notify();
      return DEFAULT;
    });
  return inflight;
}

export function usePrice() {
  const [state, setState] = useState(cache || DEFAULT);
  useEffect(() => {
    let cancel = false;
    getPrice().then((p) => {
      if (!cancel) setState(p);
    });
    const sub = (p) => {
      if (!cancel) setState(p);
    };
    subs.add(sub);
    return () => {
      cancel = true;
      subs.delete(sub);
    };
  }, []);
  return state;
}

/** Just the price token, e.g. "$7" */
export function PriceTag({ className = "" }) {
  const { price } = usePrice();
  return <span className={className} data-testid="price-tag">{price}</span>;
}

/** Full scarcity line: "1,073 of 2,500 launch spots left at $7" */
export function PriceScarcity({ className = "", prefix = "" }) {
  const { price, remaining, cap } = usePrice();
  if (!remaining || !cap) {
    return (
      <span className={className} data-testid="price-scarcity">
        {prefix}
        {price}
      </span>
    );
  }
  return (
    <span className={className} data-testid="price-scarcity">
      {prefix}
      <b>{remaining.toLocaleString()}</b> of {cap.toLocaleString()} launch spots left at{" "}
      <b>{price}</b>
    </span>
  );
}
